export interface ActivityFrequencyData {
  averages?: Record<string, number>
  data: number[][]
  visibleHours: string[]
  xAxis: string[]
  yAxis: string[]
}

export interface ActivityFrequencyMockData {
  [platform: string]: ActivityFrequencyData
  allPlatform: ActivityFrequencyData
}

const xAxis = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]

// All 24 hours - starting from 00:00
const yAxis = [
  '00:00',
  '01:00',
  '02:00',
  '03:00',
  '04:00',
  '05:00',
  '06:00',
  '07:00',
  '08:00',
  '09:00',
  '10:00',
  '11:00',
  '12:00',
  '13:00',
  '14:00',
  '15:00',
  '16:00',
  '17:00',
  '18:00',
  '19:00',
  '20:00',
  '21:00',
  '22:00',
  '23:00',
]

// Hours to show labels for (to avoid overcrowding)
const visibleHours = [
  '00:00',
  '03:00',
  '06:00',
  '09:00',
  '12:00',
  '15:00',
  '18:00',
  '21:00',
]

// Helper to generate data array for heatmap
// Format: [dayIndex, hourIndex, value]
const generateHeatmapData = (values: number[][]): number[][] => {
  const data: number[][] = []
  values.forEach((row, hourIndex) => {
    row.forEach((value, dayIndex) => {
      data.push([dayIndex, hourIndex, value])
    })
  })
  return data
}

// Synced data for "Wanderlust Adventures" travel brand
// Peak activity: evenings (6-8 PM) and weekends
// Travel content performs best during leisure hours

// Posts Activity - All Platforms (24 hours x 7 days) - starting from 00:00
const postsAllPlatformValues = [
  [45, 55, 40, 50, 45, 95, 85], // 00:00
  [30, 40, 28, 35, 32, 75, 68], // 01:00
  [22, 30, 20, 26, 22, 58, 52], // 02:00
  [65, 85, 72, 90, 68, 130, 115], // 03:00
  [40, 52, 38, 48, 35, 82, 72], // 04:00
  [70, 90, 78, 95, 72, 125, 110], // 05:00
  [110, 140, 125, 150, 130, 180, 165], // 06:00
  [150, 185, 165, 195, 175, 240, 220], // 07:00
  [180, 220, 165, 195, 210, 280, 250], // 08:00
  [220, 280, 240, 265, 250, 320, 290], // 09:00
  [260, 320, 285, 310, 295, 380, 340], // 10:00
  [290, 370, 315, 345, 310, 420, 380], // 11:00
  [310, 380, 330, 360, 325, 450, 410], // 12:00
  [330, 410, 355, 385, 350, 480, 440], // 13:00
  [350, 420, 380, 410, 375, 510, 470], // 14:00
  [340, 400, 365, 395, 360, 490, 450], // 15:00
  [360, 420, 385, 415, 380, 520, 480], // 16:00
  [390, 450, 410, 445, 405, 560, 520], // 17:00
  [420, 490, 450, 485, 445, 620, 580], // 18:00 - Peak evening
  [450, 520, 480, 520, 475, 680, 640], // 19:00 - Peak evening
  [430, 500, 460, 495, 450, 650, 610], // 20:00
  [380, 440, 400, 435, 395, 580, 540], // 21:00
  [320, 370, 340, 365, 330, 490, 450], // 22:00
  [260, 300, 275, 295, 270, 400, 365], // 23:00
]

// Posts Activity - Facebook (24 hours x 7 days) - starting from 00:00
const postsFacebookValues = [
  [22, 27, 19, 24, 22, 46, 41], // 00:00
  [15, 20, 14, 17, 16, 37, 33], // 01:00
  [11, 15, 10, 13, 11, 28, 25], // 02:00
  [32, 42, 35, 44, 33, 64, 56], // 03:00
  [20, 26, 19, 24, 17, 40, 35], // 04:00
  [34, 44, 38, 46, 35, 61, 54], // 05:00
  [54, 68, 61, 73, 64, 88, 81], // 06:00
  [73, 90, 80, 95, 85, 117, 107], // 07:00
  [85, 105, 78, 92, 100, 135, 120], // 08:00
  [105, 135, 115, 128, 120, 155, 140], // 09:00
  [125, 155, 138, 150, 142, 185, 165], // 10:00
  [140, 180, 152, 167, 150, 205, 185], // 11:00
  [150, 185, 160, 175, 158, 220, 200], // 12:00
  [160, 200, 172, 187, 170, 235, 215], // 13:00
  [170, 205, 185, 200, 182, 250, 230], // 14:00
  [165, 195, 178, 192, 175, 240, 220], // 15:00
  [175, 205, 188, 202, 185, 255, 235], // 16:00
  [190, 220, 200, 217, 198, 275, 255], // 17:00
  [205, 240, 220, 237, 217, 305, 285], // 18:00
  [220, 255, 235, 255, 232, 335, 315], // 19:00
  [210, 245, 225, 242, 220, 320, 300], // 20:00
  [185, 215, 195, 212, 193, 285, 265], // 21:00
  [155, 180, 165, 178, 160, 240, 220], // 22:00
  [125, 145, 133, 143, 130, 195, 178], // 23:00
]

// Posts Activity - Instagram (24 hours x 7 days) - starting from 00:00
const postsInstagramValues = [
  [16, 20, 15, 18, 16, 35, 31], // 00:00
  [11, 15, 10, 13, 12, 28, 25], // 01:00
  [8, 11, 7, 10, 8, 22, 19], // 02:00
  [24, 31, 26, 33, 25, 47, 42], // 03:00
  [15, 19, 14, 17, 13, 30, 26], // 04:00
  [26, 33, 28, 35, 26, 46, 40], // 05:00
  [40, 51, 46, 55, 47, 66, 60], // 06:00
  [55, 68, 60, 71, 64, 88, 80], // 07:00
  [65, 80, 60, 72, 77, 102, 92], // 08:00
  [80, 102, 88, 96, 92, 117, 106], // 09:00
  [95, 117, 104, 113, 108, 138, 124], // 10:00
  [106, 135, 115, 126, 113, 153, 138], // 11:00
  [113, 138, 120, 131, 118, 164, 149], // 12:00
  [120, 149, 129, 140, 127, 175, 160], // 13:00
  [128, 153, 138, 149, 136, 186, 171], // 14:00
  [124, 145, 133, 144, 131, 178, 164], // 15:00
  [131, 153, 140, 151, 138, 189, 175], // 16:00
  [142, 164, 149, 162, 147, 203, 189], // 17:00
  [153, 178, 164, 177, 162, 225, 210], // 18:00
  [164, 189, 175, 189, 173, 246, 233], // 19:00
  [157, 182, 167, 180, 164, 236, 224], // 20:00
  [138, 160, 146, 158, 144, 210, 196], // 21:00
  [117, 135, 124, 133, 120, 178, 164], // 22:00
  [95, 110, 100, 107, 99, 146, 133], // 23:00
]

// Posts Activity - TikTok (24 hours x 7 days) - starting from 00:00
const postsTiktokValues = [
  [7, 8, 6, 8, 7, 14, 13], // 00:00
  [4, 5, 4, 5, 4, 10, 10], // 01:00
  [3, 4, 3, 3, 3, 8, 8], // 02:00
  [9, 12, 11, 13, 10, 19, 17], // 03:00
  [5, 7, 5, 7, 5, 12, 11], // 04:00
  [10, 13, 12, 14, 11, 18, 16], // 05:00
  [16, 21, 18, 22, 19, 26, 24], // 06:00
  [22, 27, 25, 29, 26, 35, 33], // 07:00
  [30, 35, 27, 31, 33, 43, 38], // 08:00
  [35, 43, 37, 41, 38, 48, 44], // 09:00
  [40, 48, 43, 47, 45, 57, 51], // 10:00
  [44, 55, 48, 52, 47, 62, 57], // 11:00
  [47, 57, 50, 54, 49, 66, 61], // 12:00
  [50, 61, 54, 58, 53, 70, 65], // 13:00
  [52, 62, 57, 61, 57, 74, 69], // 14:00
  [51, 60, 54, 59, 54, 72, 66], // 15:00
  [54, 62, 57, 62, 57, 76, 70], // 16:00
  [58, 66, 61, 66, 60, 82, 76], // 17:00
  [62, 72, 66, 71, 66, 90, 85], // 18:00
  [66, 76, 70, 76, 70, 99, 92], // 19:00 - Peak TikTok
  [63, 73, 68, 73, 66, 94, 86], // 20:00
  [57, 65, 59, 65, 58, 85, 79], // 21:00
  [48, 55, 51, 54, 50, 72, 66], // 22:00
  [40, 45, 42, 45, 41, 59, 54], // 23:00
]

// Engagement Activity - All Platforms (24 hours x 7 days) - starting from 00:00
const engagementAllPlatformValues = [
  [480, 590, 430, 535, 480, 1020, 910], // 00:00
  [320, 420, 300, 375, 340, 800, 720], // 01:00
  [235, 320, 215, 280, 235, 620, 555], // 02:00
  [695, 910, 770, 960, 730, 1400, 1230], // 03:00
  [430, 560, 410, 515, 375, 880, 770], // 04:00
  [750, 960, 835, 1020, 770, 1340, 1180], // 05:00
  [1180, 1500, 1340, 1600, 1400, 1930, 1770], // 06:00
  [1600, 1980, 1770, 2090, 1880, 2580, 2360], // 07:00
  [1850, 2280, 1720, 2020, 2180, 2950, 2650], // 08:00
  [2280, 2950, 2520, 2780, 2650, 3400, 3080], // 09:00
  [2720, 3400, 3020, 3280, 3120, 4050, 3620], // 10:00
  [3080, 3950, 3360, 3680, 3320, 4500, 4080], // 11:00
  [3320, 4080, 3520, 3850, 3480, 4820, 4400], // 12:00
  [3520, 4400, 3800, 4120, 3750, 5150, 4720], // 13:00
  [3750, 4500, 4080, 4400, 4020, 5480, 5050], // 14:00
  [3620, 4280, 3900, 4220, 3850, 5250, 4820], // 15:00
  [3850, 4500, 4120, 4450, 4080, 5580, 5150], // 16:00
  [4180, 4820, 4400, 4780, 4350, 6020, 5580], // 17:00
  [4500, 5250, 4820, 5200, 4780, 6680, 6220], // 18:00 - Peak
  [4820, 5580, 5150, 5580, 5100, 7320, 6880], // 19:00 - Peak
  [4620, 5380, 4950, 5320, 4850, 7000, 6580], // 20:00
  [4080, 4720, 4300, 4680, 4250, 6250, 5800], // 21:00
  [3420, 3980, 3650, 3920, 3550, 5280, 4850], // 22:00
  [2780, 3220, 2950, 3180, 2900, 4300, 3920], // 23:00
]

// Engagement Activity - Facebook (24 hours x 7 days) - starting from 00:00
const engagementFacebookValues = [
  [185, 230, 170, 210, 185, 400, 355], // 00:00
  [125, 165, 115, 145, 135, 310, 280], // 01:00
  [90, 125, 85, 110, 90, 240, 215], // 02:00
  [270, 355, 300, 375, 285, 545, 480], // 03:00
  [170, 220, 160, 200, 145, 345, 300], // 04:00
  [295, 375, 325, 400, 300, 520, 460], // 05:00
  [460, 585, 520, 625, 545, 755, 690], // 06:00
  [625, 770, 690, 815, 730, 1005, 920], // 07:00
  [720, 890, 670, 790, 850, 1150, 1035], // 08:00
  [890, 1150, 985, 1085, 1035, 1330, 1200], // 09:00
  [1060, 1330, 1180, 1280, 1220, 1580, 1415], // 10:00
  [1200, 1540, 1310, 1435, 1295, 1755, 1590], // 11:00
  [1295, 1590, 1375, 1500, 1355, 1880, 1715], // 12:00
  [1375, 1715, 1480, 1605, 1460, 2010, 1840], // 13:00
  [1460, 1755, 1590, 1715, 1570, 2135, 1965], // 14:00
  [1415, 1670, 1520, 1645, 1500, 2050, 1880], // 15:00
  [1500, 1755, 1605, 1735, 1590, 2180, 2010], // 16:00
  [1630, 1880, 1715, 1865, 1700, 2350, 2180], // 17:00
  [1755, 2050, 1880, 2030, 1865, 2605, 2425], // 18:00
  [1880, 2180, 2010, 2180, 1990, 2855, 2685], // 19:00
  [1800, 2100, 1930, 2075, 1890, 2730, 2570], // 20:00
  [1590, 1840, 1675, 1825, 1660, 2440, 2260], // 21:00
  [1335, 1550, 1425, 1530, 1385, 2060, 1890], // 22:00
  [1085, 1255, 1150, 1240, 1130, 1675, 1530], // 23:00
]

// Engagement Activity - Instagram (24 hours x 7 days) - starting from 00:00
const engagementInstagramValues = [
  [175, 220, 160, 200, 175, 380, 340], // 00:00
  [115, 155, 110, 140, 125, 295, 265], // 01:00
  [85, 115, 80, 100, 85, 230, 205], // 02:00
  [255, 335, 285, 355, 270, 515, 455], // 03:00
  [160, 205, 150, 190, 140, 325, 285], // 04:00
  [275, 355, 310, 375, 285, 495, 435], // 05:00
  [435, 555, 495, 590, 515, 715, 655], // 06:00
  [590, 730, 655, 770, 695, 955, 870], // 07:00
  [680, 840, 635, 745, 800, 1085, 975], // 08:00
  [840, 1085, 930, 1025, 975, 1255, 1135], // 09:00
  [1000, 1255, 1115, 1210, 1150, 1495, 1335], // 10:00
  [1135, 1455, 1240, 1355, 1225, 1660, 1500], // 11:00
  [1225, 1500, 1300, 1420, 1280, 1780, 1620], // 12:00
  [1300, 1620, 1400, 1520, 1380, 1900, 1740], // 13:00
  [1380, 1660, 1500, 1620, 1480, 2020, 1860], // 14:00
  [1335, 1580, 1440, 1555, 1420, 1940, 1780], // 15:00
  [1420, 1660, 1520, 1640, 1500, 2060, 1900], // 16:00
  [1540, 1780, 1620, 1760, 1605, 2220, 2060], // 17:00
  [1660, 1940, 1780, 1920, 1760, 2465, 2295], // 18:00
  [1780, 2060, 1900, 2060, 1880, 2710, 2540], // 19:00
  [1705, 1985, 1825, 1965, 1790, 2590, 2430], // 20:00
  [1500, 1740, 1585, 1725, 1570, 2300, 2140], // 21:00
  [1260, 1465, 1345, 1445, 1310, 1940, 1785], // 22:00
  [1025, 1190, 1085, 1170, 1070, 1580, 1445], // 23:00
]

// Engagement Activity - TikTok (24 hours x 7 days) - starting from 00:00
const engagementTiktokValues = [
  [120, 140, 100, 125, 120, 240, 215], // 00:00
  [80, 100, 75, 90, 80, 195, 175], // 01:00
  [60, 80, 50, 70, 60, 150, 135], // 02:00
  [170, 220, 185, 230, 175, 340, 295], // 03:00
  [100, 135, 100, 125, 90, 210, 185], // 04:00
  [180, 230, 200, 245, 185, 325, 285], // 05:00
  [285, 360, 325, 385, 340, 460, 425], // 06:00
  [385, 480, 425, 505, 455, 620, 570], // 07:00
  [450, 550, 415, 485, 530, 715, 640], // 08:00
  [550, 715, 605, 670, 640, 815, 745], // 09:00
  [660, 820, 725, 790, 750, 975, 870], // 10:00
  [745, 955, 810, 890, 800, 1085, 990], // 11:00
  [800, 990, 845, 930, 845, 1160, 1065], // 12:00
  [845, 1065, 920, 995, 910, 1240, 1140], // 13:00
  [910, 1085, 990, 1065, 970, 1325, 1225], // 14:00
  [870, 1030, 940, 1020, 930, 1260, 1160], // 15:00
  [930, 1085, 995, 1075, 990, 1340, 1240], // 16:00
  [1010, 1160, 1065, 1155, 1045, 1450, 1340], // 17:00
  [1085, 1260, 1160, 1250, 1155, 1610, 1500], // 18:00
  [1160, 1340, 1240, 1340, 1230, 1755, 1655], // 19:00 - Peak TikTok
  [1115, 1295, 1195, 1280, 1170, 1680, 1580], // 20:00
  [990, 1140, 1040, 1130, 1020, 1510, 1400], // 21:00
  [825, 965, 880, 945, 855, 1280, 1175], // 22:00
  [670, 775, 715, 770, 700, 1045, 945], // 23:00
]

export const activityFrequencyPostsMockData: ActivityFrequencyMockData = {
  allPlatform: {
    data: generateHeatmapData(postsAllPlatformValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  facebook: {
    data: generateHeatmapData(postsFacebookValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  instagram: {
    data: generateHeatmapData(postsInstagramValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  tiktok: {
    data: generateHeatmapData(postsTiktokValues),
    visibleHours,
    xAxis,
    yAxis,
  },
}

export const activityFrequencyEngagementMockData: ActivityFrequencyMockData = {
  allPlatform: {
    data: generateHeatmapData(engagementAllPlatformValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  facebook: {
    data: generateHeatmapData(engagementFacebookValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  instagram: {
    data: generateHeatmapData(engagementInstagramValues),
    visibleHours,
    xAxis,
    yAxis,
  },
  tiktok: {
    data: generateHeatmapData(engagementTiktokValues),
    visibleHours,
    xAxis,
    yAxis,
  },
}
